<template>
  <NonScDrawer :open="drawer.open" @closed="() => (drawer.open = false)">
    <template slot="body">
      <div class="drawer-body">
        <div class="editor-header">
          <h4 class="editor-title">
            {{ title }}
          </h4>
        </div>
        <div class="editor-form">
          <div>
            <label>
              <i v-if="valid.structure" class="el-icon-circle-check valid" />
              <i v-else class="el-icon-warning invalid" /> ประเภทสิ่งก่อสร้าง
            </label>
            <br />
            <div class="group">
              <el-button
                v-for="(option, i) in construct"
                :key="i"
                :type="'sc-asset'"
                :inactive="input.structure != option.name"
                @click="() => (input.structure = option.name)"
              >
                {{ option.name }}
              </el-button>
            </div>
          </div>

          <div class="form grid-2">
            <div>
              <label>
                <i v-if="valid.addressNo" class="el-icon-circle-check valid" />
                <i v-else class="el-icon-warning invalid" /> เลขที่
              </label>
              <input
                v-model="input.addressNo"
                :disabled="mode == 'update'"
                :placeholder="'ระบุเลขที่อยู่ 99, 1/99, หรือ 1/9-9'"
                type="text"
                name="addressNo"
                class="input el-input__inner"
              />
            </div>

            <div>
              <label> ถนน </label>
              <input
                v-model="input.addressRoad"
                :placeholder="'ระบุถนน'"
                type="text"
                name="addressRoad"
                class="input el-input__inner"
              />
            </div>
          </div>

          <div class="form grid-2">
            <div>
              <label> พื้นที่ใช้สอย (ตร.ม.) </label>
              <input
                v-model="input.area"
                :placeholder="'ระบุตัวเลข 1 - 2000 (ตร.ม.)'"
                type="number"
                name="area"
                min="1"
                max="2000"
                step="any"
                class="input el-input__inner"
              />
              <small v-if="!valid.area" class="error-text">
                กรุณาระบุพื้นที่ใช้สอย 1 - 2000 ตร.ม.
              </small>
            </div>
            <div>
              <label>
                <i
                  v-if="valid.transferDate"
                  class="el-icon-circle-check valid"
                />
                <i v-else class="el-icon-warning invalid" /> วันที่โอน
              </label>
              <input
                v-model="input.transferDate"
                type="date"
                name="transferDate"
                class="input el-input__inner"
              />
              <small
                v-if="mode == 'update' && !input.transferDate && form.transferDate"
                class="error-text"
              >
                ไม่สามารถลบวันที่โอนที่มีอยู่แล้วได้
              </small>
            </div>
          </div>

          <div>
            <label>
              <i v-if="valid.address" class="el-icon-circle-check valid" />
              <i v-else class="el-icon-warning invalid" /> รายละเอียดที่อยู่
            </label>
            <NonScAddress
              :selected="input"
              :open="address.open"
              @selected="(value) => setAddress(value)"
            />
          </div>

          <div class="editor-action group">
            <el-button
              :disabled="!allow"
              class="action"
              type="sc-asset"
              @click="() => submit()"
            >
              บันทึก
            </el-button>
            <el-button
              v-if="mode == 'update'"
              class="action"
              type="danger"
              @click="() => remove()"
            >
              ลบบ้าน
            </el-button>
            <el-button
              class="action"
              type="info"
              @click="() => (drawer.open = false)"
            >
              ยกเลิก
            </el-button>
          </div>
        </div>
      </div>
    </template>
  </NonScDrawer>
</template>

<script lang="js">
// Common
import { NonScCommon as Common } from "../../common/nonsc.common"

import { NonScHomeCommon as HomeCommon } from "../../common/nonsc-home.common"

// Components
import NonScAddress from "../../components/NonScAddress"
import NonScDrawer from "../../components/NonScDrawer"

// Utils
import { ValidateUtility as Validate } from "../../utils/validate"

export default {
  components: {
    NonScAddress,
    NonScDrawer,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "create",
    },
    form: {
      type: Object,
      default: () => HomeCommon.input,
    },
  },
  data() {
    return {
      drawer: {
        open: false,
      },
      address: {
        open: false,
      },
      input: {
        ...HomeCommon.input,
      },
    }
  },
  computed: {
    title() {
      return this.mode === "create" ? "เพิ่มข้อมูลบ้าน" : "แก้ไขข้อมูลบ้าน"
    },
    construct() {
      return Common.construct
    },
    role() {
      return Common.role
    },
    valid() {
      // Validator
      const valid = {
        addressNo: false,
        address: false,
        structure: false,
        transferDate: false,
        area: false,
      }

      // Validate Input
      if (!this.input) return valid

      // Email Input
      const { addressNo, structure, transferDate, area } = this.input

      // Validate Address Number
      valid.addressNo = Validate.isAddrNo(addressNo)

      // Validate Address
      valid.address = Validate.isLocation(this.input)

      // Validate Construction type
      valid.structure = this.construct.some((e) => e.name === structure)

      // Validate Transfer Date: legacy homes may have none, so an edit may
      // keep it empty, but an existing date cannot be cleared (the backend
      // ignores an empty date and would silently keep the old one)
      valid.transferDate = transferDate
        ? Validate.isDate(transferDate)
        : this.mode === "update" && !this.form.transferDate

      // Validate Usable Area: optional, but 1 - 2000 when given
      valid.area = Validate.isEmpty(area) || Validate.isArea(area)

      return valid
    },
    allow() {
      return Object.values(this.valid).every((e) => e)
    },
  },
  watch: {
    open(value) {
      // Validate State
      if (!value) return

      // Set Drawer
      this.drawer.open = value

      // Set Input
      if (value) {
        this.input = { ...this.form }
      } else {
        setTimeout(this.reset, 400)
      }
    },
    "drawer.open": function drawerOpenWatcher(open) {
      if (!open) this.$emit("closed")
    },
  },
  mounted() {
    // Set Drawer
    this.drawer.open = this.open

    // Set Input
    this.input = {
      ...HomeCommon.input,
      ...this.form,
    }
  },
  methods: {
    reset() {
      this.input = {
        ...HomeCommon.input,
      }
    },
    setAddress(input) {
      // Set Location
      const location = Common.setLocation(input)

      // Validate Input
      if (location) {
        Object.assign(this.input, location)
      }
    },
    submit() {
      // Emit Change
      this.$emit("submit", this.input)

      // Set Drawer
      this.drawer.open = false
    },
    remove() {
      // Emit Remove
      this.$emit("remove", this.input.id)

      // Set Drawer
      this.drawer.open = false
    },
  },
}
</script>

<style lang="css" scoped>
.drawer-body {
  display: flex;
  flex-direction: column;
  height: 100svh;
}

.editor-header {
  padding: var(--size-lg);
}

.editor-title {
  margin: 0;
}

.editor-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--size-sm);
  padding: var(--size-lg);
  padding-top: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.editor-action {
  display: flex;
  margin-top: var(--size-xs);
}

.group {
  display: flex;
  gap: var(--size-xs);
  flex-wrap: wrap;
}

.editor-action.group {
  gap: var(--size-md);
}

.el-button.action {
  min-width: 5rem;
}

.error-text {
  color: #f56c6c;
}

@media screen and (max-width: 768px) {
  .editor-header {
    padding: var(--size-md);
  }

  .editor-form {
    padding: 0 var(--size-md);
  }

  .editor-title {
    text-align: right;
  }
}
</style>
